import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AntTree } from './style';
import { getChildOuData } from 'redux/slices/getChildOu';
import { fetchOuTreeData, setExpandedKeysOuAdmin, setOuAdminNodekey, setOuAdminsTree } from 'redux/slices/ouAdminTree';
import { setDomainId, setDomainName, setSelectedNode, setTypeOfData } from 'redux/slices/selectedNodeSlice';
import { getAllUsers } from 'redux/slices/userSlice';
import { getOuGroupData } from 'redux/slices/getOuGroups';
import { useLocation } from 'react-router-dom';
import { getChildOuTreeData } from 'redux/slices/treedata';
import { getDomainExportAttribute } from 'redux/slices/settingsSlice';

export const updateTreeData = (list, key, children) =>
  list.map((node) => {
    if (node.key === key) {
      return {
        ...node,
        children,
      };
    }
    if (node.children) {
      return {
        ...node,
        children: updateTreeData(node.children, key, children),
      };
    }
    return node;
  });

const OuTree = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const domain = localStorage.getItem('domain');
  const domainId = Number(localStorage.getItem('domainId'));
  const name = localStorage.getItem('dn');
  const userLoading = useSelector((state) => state.getUsers.loading);
  const groupLoading = useSelector((state) => state.getOuGroupData.loading);
  const ouLoading = useSelector((state) => state.getChildOuData.loading);
  const fqdn = useSelector((state) => state.selectedNode.domainName);
  const sideTreeData = useSelector((state) => state.ouTreeData.objectData);
  const selectedNode = useSelector((state) => state.selectedNode);
  const adminOuName = useSelector((state) => state.ouTreeData.data);
  const { ouAdminTreeExpandedKeys, ouAdminsTree, ouAdminNodeKey } = useSelector((state) => state.ouTreeData);
  const { pageGroupNumber, pageGroupSize, groupSearch, groupIsDescending, groupSortType } = useSelector(
    (state) => state.getOuGroupData
  );
  const { pageOuNumber, pageOuSize, ouSearch, ouIsDescending, ouSortType } = useSelector(
    (state) => state.getChildOuData
  );
  const [prevSelectedNode, setPrevSelectedNode] = useState(selectedNode);

  useEffect(() => {
    if (fqdn) {
      const value = { name: name, fqdn: fqdn };
      dispatch(fetchOuTreeData(value));
      dispatch(setTypeOfData('ou'));
    }
  }, [dispatch, fqdn, name]);

  useEffect(() => {
    let updatedSearchQuery;
    let updatedPageNumber;
    let updatedPageSize;
    let updatedIsDescending;
    let updatedSortType;
    if (selectedNode.selectedNode !== prevSelectedNode.selectedNode) {
      updatedSearchQuery = '';
      updatedPageNumber = 1;
      updatedPageSize = 20;
      updatedIsDescending = false;
      updatedSortType = '';
    } else {
      if (location.pathname === '/') {
        updatedSearchQuery = selectedNode.searchQuery;
        updatedPageNumber = selectedNode.pageNumber;
        updatedPageSize = selectedNode.pageSize;
        updatedIsDescending = selectedNode.isDescending;
        updatedSortType = selectedNode.sortType;
      } else if (location.pathname === '/groupdata') {
        updatedSearchQuery = groupSearch;
        updatedPageNumber = pageGroupNumber;
        updatedPageSize = pageGroupSize;
        updatedIsDescending = groupIsDescending;
        updatedSortType = groupSortType;
      } else if (location.pathname === '/oudata') {
        updatedSearchQuery = ouSearch;
        updatedPageNumber = pageOuNumber;
        updatedPageSize = pageOuSize;
        updatedIsDescending = ouIsDescending;
        updatedSortType = ouSortType;
      }
    }
    const commonValues = {
      ouName:
        selectedNode.selectedNode && selectedNode.selectedNode.dnName
          ? selectedNode.selectedNode.dnName
          : adminOuName.dnName,
      fqdn: selectedNode.domainName || localStorage.getItem('domain'),
      page: updatedPageNumber,
      pageSize: updatedPageSize,
      searchQuery: updatedSearchQuery,
      isDescending: updatedIsDescending,
      sortType: updatedSortType,
    };
    if (selectedNode.domainName && adminOuName.length !== 0) {
      if (location.pathname === '/') {
        dispatch(getAllUsers({ ...commonValues, usersFilter: selectedNode.usersFilter }));
      } else if (location.pathname === '/groupdata') {
        dispatch(getOuGroupData(commonValues));
      } else if (location.pathname === '/oudata') {
        dispatch(getChildOuData(commonValues));
      }
    }
    setPrevSelectedNode(selectedNode);
  }, [
    adminOuName,
    selectedNode.selectedNode,
    location.pathname,
    selectedNode.pageNumber,
    selectedNode.pageSize,
    selectedNode.isDescending,
    selectedNode.sortType,
    pageOuNumber,
    pageOuSize,
    ouIsDescending,
    ouSortType,
    pageGroupNumber,
    pageGroupSize,
    groupIsDescending,
    groupSortType,
  ]);

  useEffect(() => {
    dispatch(setOuAdminsTree(sideTreeData));
  }, [sideTreeData]);

  useEffect(() => {
    dispatch(setOuAdminNodekey(1));
    sideTreeData?.length && dispatch(setSelectedNode({ name: sideTreeData[0].title, dnName: sideTreeData[0].dnName }));
    dispatch(setDomainName(domain));
    dispatch(setDomainId(domainId));
    dispatch(getDomainExportAttribute(domainId));
  }, [dispatch, domain, sideTreeData]);

  const getNodeTitle = (node) => {
    const isSelected = ouAdminNodeKey === node.key;

    return (
      <span
        style={{
          color: isSelected ? 'var(--leftmenu-active-color)' : 'var(--leftmenu-text-color)',
          whiteSpace: 'nowrap',
          backgroundColor: isSelected ? 'var(--leftmenu-highlighter-color)' : 'transparent',
          borderRadius: '6px',
          padding: isSelected && '4px 12px 5px 12px',
        }}
      >
        {node.title}
      </span>
    );
  };

  const handleNodeExpand = (keys) => {
    if (userLoading || ouLoading || groupLoading) return;
    dispatch(setExpandedKeysOuAdmin(keys));
  };

  const handleNodeSelect = async (selectedKeys, info) => {
    if (userLoading || ouLoading || groupLoading) return;
    const selectedNodeData = { name: info.node.title, dnName: info.node.dnName };
    const selectedNodeInfo = info.node;
    const childData = await dispatch(
      getChildOuTreeData({ ouName: selectedNodeInfo.dnName, fqdn: selectedNode.domainName })
    );
    if (childData.payload.length === 0) {
      // If child data is empty, set the selected node's key as expanded
      dispatch(setExpandedKeysOuAdmin([...ouAdminTreeExpandedKeys, selectedNodeInfo.key]));
    }
    dispatch(setOuAdminNodekey(selectedKeys[0]));
    dispatch(setSelectedNode(selectedNodeData));
  };

  const onLoadData = async (parentName) => {
    if (userLoading || ouLoading || groupLoading) return;
    // Fetch child OUs based on the parent name
    const value = {
      fqdn: fqdn,
      ouName: parentName.dnName,
    };
    const childData = await dispatch(getChildOuTreeData(value));
    const children = childData.payload.map((data, index) => ({
      title: data.name,
      dnName: data.dnName,
      key: `${parentName.key} - ${index + 1}`,
    }));

    // Update the tree data by adding the children to the parent node
    const childerdata = await updateTreeData(ouAdminsTree, parentName.key, children);
    dispatch(setOuAdminsTree(childerdata));
  };

  return (
    <AntTree
      showLine
      disabled={userLoading || ouLoading || groupLoading}
      loadData={onLoadData}
      treeData={ouAdminsTree ? ouAdminsTree : []}
      onSelect={handleNodeSelect}
      titleRender={getNodeTitle}
      expandedKeys={ouAdminTreeExpandedKeys}
      onExpand={handleNodeExpand}
      className={
        userLoading || ouLoading || groupLoading ? 'tree-disabled af-ouadmin-tree-wrap' : 'af-ouadmin-tree-wrap'
      }
    />
  );
};
export default OuTree;
